import React, { useState } from 'react';
import { Modal } from '@/shared/ui/Modal/Modal';
import { useGetUserChangeLogQuery } from '@/entities/User';
import { Loader } from '@/shared/ui/Loader/Loader';
import { Button } from '@/shared/ui/Button/Button';
import { formatLogValue } from '../lib/formatLogValue';
import { fieldLabels } from '@/entities/User/model/constants';
import { formatDateTimeForDisplay } from '@/shared/lib/utils/dateFormat';
import { formatFullName } from '@/shared/lib/utils/formatFullName';
import { accountDeletionEventLabels } from '@/shared/constants/accountDeletion';
import s from './UserChangeLogModal.module.scss';

interface UserChangeLogModalProps {
  userId: number;
  userName: string;
  onClose: () => void;
}

const LOG_PAGE_LIMIT = 20;

export const UserChangeLogModal: React.FC<UserChangeLogModalProps> = ({ userId, userName, onClose }) => {
  const [offset, setOffset] = useState(0);
  const {
    data: logs,
    isLoading,
    isFetching,
    error,
  } = useGetUserChangeLogQuery({
    userId,
    offset,
    limit: LOG_PAGE_LIMIT,
  });

  const handleLoadMore = () => {
    if (!isFetching) {
      setOffset((prev) => prev + LOG_PAGE_LIMIT);
    }
  };

  const hasMoreData = logs && logs.length > 0 && logs.length % LOG_PAGE_LIMIT === 0;

  return (
    <Modal open onClose={onClose} title={`История изменений: ${userName}`}>
      <div className={s.root}>
        <div className={s.logContainer}>
          {isLoading && offset === 0 && (
            <div className={s.loaderWrapper}>
              <Loader />
            </div>
          )}
          {error && <div>Ошибка загрузки истории изменений</div>}

          {!isLoading && offset === 0 && logs && logs.length === 0 && (
            <div className={s.emptyWrapper}>Изменений не найдено</div>
          )}

          {logs && logs.length > 0 && (
            <ul className={s.logList}>
              {logs.map((log) => (
                <li key={log.id} className={s.logItem}>
                  <div className={s.editorInfo}>
                    <span className={s.editorName}>
                      {(log.editor as any)?.fullName ||
                        formatFullName(
                          (log.editor as any)?.lastName,
                          (log.editor as any)?.firstName,
                          (log.editor as any)?.patronymic,
                        ) ||
                        '—'}
                    </span>
                    <span className={s.date}>{formatDateTimeForDisplay(log.createdAt)}</span>
                  </div>
                  <div className={s.action}>
                    <strong>{fieldLabels[log.field] || log.field}</strong>
                    {log.type ? <span>{accountDeletionEventLabels[log.type] || log.type}</span> : null}
                    <span>с</span>
                    <span className={s.oldValue}>{formatLogValue(log.field, log.oldValue)}</span>
                    <span>на</span>
                    <span className={s.newValue}>{formatLogValue(log.field, log.newValue)}</span>
                    {log.reason ? <span>Причина: {log.reason}</span> : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {hasMoreData && (
          <Button onClick={handleLoadMore} disabled={isFetching} className={s.loadMoreBtn}>
            {isFetching ? 'Загрузка...' : 'Показать ещё'}
          </Button>
        )}
      </div>
    </Modal>
  );
};
